/**
 * React Query hooks for draft operations
 */

import type { ApprovalResult, InvoiceDraft } from "@eoln/sdk-invoices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInvoicesClient } from "./useInvoicesClient";

type DraftStatus = "DRAFT_CREATED" | "DRAFT_UPDATED" | "PENDING_APPROVAL" | "ISSUED" | "REJECTED" | "DISCARDED";
type DraftType = "INVOICE" | "CORRECTION";
type IssueType = "PROFORMA" | "VAT";

interface UseDraftsOptions {
  status?: DraftStatus;
  draftType?: DraftType;
  issueType?: IssueType;
  limit?: number;
  offset?: number;
  registryId?: string;
}

/**
 * Hook to fetch drafts with optional filtering
 */
export function useDrafts(options: UseDraftsOptions = {}) {
  const client = useInvoicesClient();
  const { status, draftType, issueType, limit = 50, offset = 0, registryId } = options;

  return useQuery({
    queryKey: ["drafts", { status, draftType, issueType, limit, offset, registryId }],
    queryFn: async () => {
      console.log("[useDrafts] Fetching drafts...", { status, draftType, issueType, limit, offset, registryId });
      const filter: {
        status?: DraftStatus;
        draftType?: DraftType;
        issueType?: IssueType;
        targetRegistryId?: string;
      } = {};
      if (status) {
        filter.status = status;
      }
      if (draftType) {
        filter.draftType = draftType;
      }
      if (issueType) {
        filter.issueType = issueType;
      }
      if (registryId) {
        filter.targetRegistryId = registryId;
      }
      const result = await client.listDrafts({
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        pagination: { limit, offset },
      });
      console.log("[useDrafts] Fetched", result.total, "drafts");
      return result;
    },
    refetchInterval: 30000, // Poll every 30 seconds for new drafts
  });
}

/**
 * Hook to get count of pending drafts (for badge)
 */
export function usePendingDraftsCount() {
  const { data } = useDrafts({ status: "PENDING_APPROVAL", limit: 1 });
  return data?.total ?? 0;
}

/**
 * Hook to get count of pending correction drafts (for badge)
 */
export function usePendingCorrectionsCount() {
  const { data } = useDrafts({ status: "PENDING_APPROVAL", draftType: "CORRECTION", limit: 1 });
  return data?.total ?? 0;
}

/**
 * Hook to fetch a single draft by ID
 */
export function useDraft(draftId: string | null) {
  const client = useInvoicesClient();

  return useQuery({
    queryKey: ["draft", draftId],
    queryFn: async () => {
      if (!draftId) {
        return null;
      }
      console.log("[useDraft] Fetching draft...", draftId);
      const result = await client.getDraft(draftId);
      console.log("[useDraft] Fetched draft:", result?.status);
      return result;
    },
    enabled: !!draftId,
  });
}

/**
 * Hook to approve a draft (issues the invoice)
 */
export function useApproveDraft() {
  const client = useInvoicesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ draftId }: { draftId: string }): Promise<ApprovalResult> => {
      console.log("[useApproveDraft] Approving draft...", draftId);
      const result = await client.approveDraft(draftId, { id: "user", type: "USER" });
      console.log("[useApproveDraft] Draft approved, issued:", result.serialNumber);
      return result;
    },
    onSuccess: () => {
      // Invalidate both drafts and invoices since a new invoice was created
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceCount"] });
    },
  });
}

/**
 * Hook to reject a draft with a reason
 */
export function useRejectDraft() {
  const client = useInvoicesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ draftId, reason }: { draftId: string; reason: string }): Promise<InvoiceDraft> => {
      console.log("[useRejectDraft] Rejecting draft...", draftId, reason);
      const result = await client.rejectDraft(draftId, reason, { id: "user", type: "USER" });
      console.log("[useRejectDraft] Draft rejected:", result.status);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
    },
  });
}

/**
 * Hook to discard a draft permanently
 */
export function useDiscardDraft() {
  const client = useInvoicesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ draftId }: { draftId: string }): Promise<InvoiceDraft> => {
      console.log("[useDiscardDraft] Discarding draft...", draftId);
      const result = await client.discardDraft(draftId, { id: "user", type: "USER" });
      console.log("[useDiscardDraft] Draft discarded:", result.status);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
    },
  });
}

/**
 * Hook to update draft's target registry
 */
export function useUpdateDraftRegistry() {
  const client = useInvoicesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      draftId,
      targetRegistryId,
      version,
    }: {
      draftId: string;
      targetRegistryId: string;
      version: number;
    }): Promise<InvoiceDraft> => {
      console.log("[useUpdateDraftRegistry] Updating draft registry...", { draftId, targetRegistryId, version });
      const result = await client.updateDraft(draftId, { targetRegistryId, version }, { id: "user", type: "USER" });
      console.log("[useUpdateDraftRegistry] Draft updated:", {
        draftId: result.draftId,
        targetRegistryId: result.targetRegistryId,
        newVersion: result.version,
      });
      return result;
    },
    onSuccess: (data) => {
      console.log("[useUpdateDraftRegistry] Mutation success, invalidating queries...");
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
      queryClient.invalidateQueries({ queryKey: ["draft", data.draftId] });
    },
    onError: (error) => {
      console.error("[useUpdateDraftRegistry] Mutation failed:", error);
    },
  });
}
